<template>
  <div>
    <RedirectModal
      :show="showRedirectModal"
      @close="showRedirectModal = false"
    />
    <VTextField
      v-model="name"
      disabled
      :label="$t('forms.labels.name')"
      :rules="{ required: false }"
    />
    <VTextField
      v-model="type"
      disabled
      :label="$t('forms.labels.type')"
      :rules="{ required: false }"
    />

    <v-simple-table
      v-for="(section, i) in mutableSections"
      :key="i"
    >
      <template #default>
        <thead>
          <tr>
            <th>
              {{ section.name }}
            </th>
            <th width="10%">
              <b>{{ $t('riskAnalysis.no') }}</b>
            </th>
            <th width="10%">
              <b>{{ $t('riskAnalysis.yes') }}</b>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="field in section.fields"
            :key="field.key"
          >
            <td>
              {{ field.label }}
            </td>
            <td>
              <v-checkbox
                v-model="field.value"
                :disabled="field.disabled || !editable"
                :falseValue="null"
                :trueValue="false"
                color="primary"
              />
            </td>
            <td>
              <v-checkbox
                v-model="field.value"
                :disabled="field.disabled || !editable"
                :falseValue="null"
                :trueValue="true"
                color="primary"
              />
            </td>
          </tr>
        </tbody>
        <br>
      </template>
    </v-simple-table>

    <v-simple-table>
      <template #default>
        <tbody>
          <td>
            <b>{{ $t('riskAnalysis.riskAssessment') }} /{{ maxTotal }}</b>
          </td>
          <td width="20%">
            <b>{{ calculatedTotal }}</b>
          </td>
        </tbody>
      </template>
    </v-simple-table>

    <div class="pl-4">
      <div v-if="interventions.length">
        <div class="text-danger">
          {{ $t('riskAnalysis.interventionsMsg') }}
        </div>
        <div
          v-for="(intervention, i) in interventions"
          :key="i"
        >
          {{ intervention }}
        </div>
      </div>

      <div v-if="suggestions.length">
        <div class="text-danger">
          {{ $t('riskAnalysis.suggestionsMsg') }}
        </div>
        <div
          v-for="(suggestion, i) in suggestions"
          :key="i"
        >
          {{ suggestion }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import RedirectModal from '@/components/shared/RedirectModal.vue';
import lockService from '@/utilities/lockService';


export default {
    components: {
        VTextField,
        RedirectModal,
    },
    props: {
        name: {
            type: String,
            default: '',
        },
        type: {
            type: String,
            default: '',
        },
        sections: {
            type: Array,
            default: () => [],
        },
        interventions: {
            type: Array,
            default: () => [],
        },
        suggestions: {
            type: Array,
            default: () => [],
        },
        editable: {
            type: Boolean,
            default: true,
        },
        sum: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            showRedirectModal: false,
            mutableSections: this.sections,
            localComponentId: null,
        };
    },
    computed: {
        maxTotal() {
            return this.sections.reduce((maxTotal, section) => maxTotal + section.fields.reduce((a, b) => a + b.yesValue + b.noValue, 0), 0);
        },
        calculatedTotal() {
            let calculatedTotal = 0;

            this.sections.forEach(section => {
                section.fields.forEach(field => {
                    if (field.value) {
                        calculatedTotal += field.yesValue;
                    } else if (field.value === false) {
                        calculatedTotal += field.noValue;
                    }
                });
            });

            return calculatedTotal;
        },
    },
    watch: {
        mutableSections: {
            handler() {
                this.$emit('check', this.mutableSections);
            },
            deep: true,
        },
    },
    mounted() {
        window.addEventListener('beforeunload', this.handleUnload);
    },
    async created() {
        this.localComponentId = this.$route.params.componentId || this.component?.id;
        if (!this.$store.getters.lockFrom) {
            await this.checkLockOnLoad();
        }
    },

    beforeDestroy() {
        window.removeEventListener('beforeunload', this.handleUnload);
    },

    async beforeRouteLeave(to, from, next) {
        if (this.localComponentId) {
            const releaseStatus = await lockService.releaseLock(this.localComponentId, 'riskAnalysisComponent');
            if (!releaseStatus) return next(false);

            return next();
        }
        return next();
    },

    methods: {
        handleUnload() {
            if (this.localComponentId) {
                lockService.releaseLock(this.localComponentId, 'riskAnalysisComponent');
            }
        },
        async checkLockOnLoad() {
            try {
                const lockAvailable = await lockService.getLockStatus(this.localComponentId, 'riskAnalysisComponent');
                if (!lockAvailable) {
                    this.showRedirectModal = true;
                    return;
                }
                await lockService.requestLock(this.localComponentId, 'riskAnalysisComponent');
            } catch (error) {
                this.showRedirectModal = true;
            }
        },
    },
};
</script>
